// ============================================================
// Centralized API Client for Smplify Production Backend
// ============================================================

import { ErrorResponse, ResponseDTO } from '@/types/api';

// Base URL — update this to your production API domain
const API_BASE_URL = 'https://api.smplify.com';

// Token storage keys
// IMPORTANT: `smplify_auth_token` is the *admin API* JWT issued by
// `/api/v1/admin/authenticate`. The MCP OAuth flow issues a *different*
// token that is only accepted by EDR/Lighthouse — it must NEVER be
// written into TOKEN_KEY or `/api/v1/*` requests will 401.
const TOKEN_KEY = 'smplify_auth_token';
const MCP_TOKEN_KEY = 'smplify_mcp_access_token';
const TENANT_KEY = 'smplify_tenant_id';

function normalizeAuthToken(token: string | null): string | null {
  let trimmed = token?.trim();
  if (!trimmed) return null;
  if ((trimmed.startsWith('"') && trimmed.endsWith('"')) || (trimmed.startsWith("'") && trimmed.endsWith("'"))) {
    trimmed = trimmed.slice(1, -1).trim();
  }
  return trimmed.replace(/^Bearer\s+/i, '');
}

// --- Admin API token (api.smplify.com / `/api/v1/*`) ---

export function setAuthToken(token: string) {
  const normalized = normalizeAuthToken(token);
  if (normalized) localStorage.setItem(TOKEN_KEY, normalized);
}

export function getAuthToken(): string | null {
  const raw = localStorage.getItem(TOKEN_KEY);
  const normalized = normalizeAuthToken(raw);
  if (raw && normalized && raw !== normalized) {
    localStorage.setItem(TOKEN_KEY, normalized);
  }
  return normalized;
}

export function clearAuthToken() {
  localStorage.removeItem(TOKEN_KEY);
}

// --- MCP OAuth token (mcp.smplify.com — used by EDR/Lighthouse only) ---

export function setMcpAccessToken(token: string) {
  const normalized = normalizeAuthToken(token);
  if (normalized) localStorage.setItem(MCP_TOKEN_KEY, normalized);
}

export function getMcpAccessToken(): string | null {
  return normalizeAuthToken(localStorage.getItem(MCP_TOKEN_KEY));
}

export function clearMcpAccessToken() {
  localStorage.removeItem(MCP_TOKEN_KEY);
}

// --- Tenant ID ---

export function setTenantId(tenantId: string) {
  localStorage.setItem(TENANT_KEY, tenantId);
}

export function getTenantId(): string | null {
  return localStorage.getItem(TENANT_KEY);
}

// --- API Error ---

export class ApiError extends Error {
  status: number;
  errorCode: string;
  path: string;

  constructor(err: ErrorResponse | Record<string, unknown>) {
    // The API returns errors in two shapes:
    //   1. Legacy flat:   { status, errorCode, message, path }
    //   2. Wrapped:       { success: false, path, error: { code, message, details }, timestamp }
    // Pull the message/code from whichever is present so callers don't get an
    // empty `Error.message` (which made Save look unresponsive — see issue
    // where users couldn't tell why the API rejected their request).
    const wrapped = (err as { error?: { code?: string; message?: string } }).error;
    const message =
      (err as ErrorResponse).message ||
      wrapped?.message ||
      "Request failed. Please try again.";
    const errorCode =
      (err as ErrorResponse).errorCode ||
      wrapped?.code ||
      "UNKNOWN";
    const status =
      typeof (err as ErrorResponse).status === "number"
        ? (err as ErrorResponse).status
        : 0;
    const path = ((err as ErrorResponse).path as string) || "";

    super(message);
    this.name = "ApiError";
    this.status = status;
    this.errorCode = errorCode;
    this.path = path;
  }
}

// --- Core request function ---

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  params?: Record<string, string | number | boolean | undefined>;
  includeTenantHeader?: boolean;
  rawResponse?: boolean;
}

async function request<T>(
  path: string,
  options: RequestOptions = {}
): Promise<T> {
  const { body, params, includeTenantHeader = false, rawResponse = false, ...init } = options;

  // Build URL with query params
  let url = `${API_BASE_URL}${path}`;
  if (params) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.append(key, String(value));
      }
    });
    const qs = searchParams.toString();
    if (qs) url += `?${qs}`;
  }

  // Build headers
  const headers: Record<string, string> = {
    'X-Partner': 'portal',
    ...(init.headers as Record<string, string>),
  };

  if (includeTenantHeader) {
    const tenantId = getTenantId();
    if (tenantId) {
      headers['X-Tenant-Id'] = tenantId;
    }
  }

  // Set content type for JSON bodies
  if (body && !(body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  const executeFetch = async () => {
    // Prefer the legacy admin-login token when present; otherwise fall back
    // to the MCP OAuth bearer (issued by mcp.smplify.com with
    // resource=https://api.smplify.com). The MCP server uses exactly this
    // token against api.smplify.com — the portal does the same.
    const adminToken = getAuthToken();
    const mcpToken = adminToken ? null : getMcpAccessToken();
    const bearer = adminToken || mcpToken;
    if (bearer) {
      headers['Authorization'] = `Bearer ${bearer}`;
    } else {
      delete headers['Authorization'];
    }
    const res = await fetch(url, {
      ...init,
      headers,
      body: body instanceof FormData ? body : body ? JSON.stringify(body) : undefined,
    });
    return { res, usedMcp: !adminToken && !!mcpToken };
  };

  let response: Response;
  let usedMcp = false;
  try {
    const out = await executeFetch();
    response = out.res;
    usedMcp = out.usedMcp;
  } catch (networkError) {
    throw new Error(
      'Unable to connect to the server. This may be a network issue or a CORS restriction. Please check your internet connection and try again, or contact your administrator.'
    );
  }

  // Single refresh+retry for MCP-backed requests on 401. We do NOT retry
  // admin-token requests, to avoid an infinite refresh loop.
  if (response.status === 401 && usedMcp) {
    try {
      const { refreshAccessToken } = await import('./mcpOAuth');
      const refreshed = await refreshAccessToken();
      if (refreshed) {
        const retry = await executeFetch();
        response = retry.res;
      }
    } catch {
      /* fall through */
    }
  }

  if (response.status === 401) {
    console.warn('[apiClient] API rejected bearer token', {
      path,
      usedMcp,
      hasAdminToken: Boolean(getAuthToken()),
      hasMcpToken: Boolean(getMcpAccessToken()),
      hasTenantHeader: Boolean(headers['X-Tenant-Id']),
    });
  }


  // Handle 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }

  // Handle binary responses
  if (rawResponse) {
    return response as unknown as T;
  }

  const json = await response.json();

  if (!response.ok) {
    throw new ApiError(json as ErrorResponse);
  }

  return json as T;
}

// --- Convenience methods ---

export async function apiGet<T>(path: string, options?: Omit<RequestOptions, 'method'>): Promise<T> {
  return request<T>(path, { ...options, method: 'GET' });
}

export async function apiPost<T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
  return request<T>(path, { ...options, method: 'POST', body });
}

export async function apiPut<T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
  return request<T>(path, { ...options, method: 'PUT', body });
}

export async function apiDelete<T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
  return request<T>(path, { ...options, method: 'DELETE', body });
}

// --- Helper to unwrap ResponseDTO envelope ---

export function unwrap<T>(response: ResponseDTO<T>): T {
  if ('success' in response && !(response as any).success) {
    throw new Error((response as any).error?.message || 'Request failed');
  }
  return response.data;
}

